import { useNavigate } from 'react-router-dom';
import { useCockpit, useDataHealth } from '@/lib/query';
import { fmt, ts } from '@/lib/format';
import { Badge, Card, ProcessChain, SectionTitle, SubsystemCard } from '@/components/ui';

const SUBSYS_ROUTE: Record<string, string> = {
  'sub-src': '/makeup',
  'ras-7': '/hang',
  'hang-farm': '/hang',
  makeup: '/makeup',
  aux: '/utilities',
};

export default function Cockpit() {
  const { data, isLoading } = useCockpit();
  const nav = useNavigate();
  if (isLoading || !data) return <Card className="h-64 animate-pulse" />;

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-[26px] font-bold tracking-tight text-slate-900">数据驾驶舱</h2>
          <p className="mt-1.5 text-[14px] text-slate-500">
            总览全厂公辅、源水/补水、吊养与 7 号代表数据；点工艺节点或下区分区可进入子系统。
          </p>
        </div>
        <div className="flex flex-col items-end gap-1 text-[13px] text-slate-400">
          <div>刷新 ≤5s · 接口数据</div>
          <DataHealthChip />
        </div>
      </div>

      {/* Top: makeup + oxygen pressure */}
      <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="p-6 lg:col-span-2">
          <div className="text-[15px] font-semibold text-slate-800">补水液位与总流量</div>
          <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-[15px] text-slate-700">
            <span>120m³ 蓄水池 <b className="tabular-nums">{data.makeup.reservoirPct}%</b></span>
            <span>3 号帆布桶 <b className="tabular-nums">{data.makeup.canvasTankPct}%</b></span>
            <span>10m³ 单罐高位缓冲塔 <b className="tabular-nums">{data.makeup.bufferTowerPct}%</b></span>
          </div>
          <div className="mt-5 flex items-center gap-3 border-t border-hairline pt-4 text-[15px] text-slate-700">
            源水总补水瞬时流量 <b className="tabular-nums">{fmt(data.makeup.sourceFlowM3h, 1)} m³/h</b>
            {data.makeup.lowPressureFuse ? <Badge className="bg-alarm-bg text-alarm-fg border-alarm-border">低压熔断</Badge> : null}
          </div>
        </Card>
        <Card className="flex flex-col justify-center p-6">
          <div className="text-[15px] font-semibold text-slate-800">公辅 · 主路纯氧压力</div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-[40px] font-bold leading-none tabular-nums text-slate-900">{data.utilityOxygenPressureMpa.toFixed(3)}</span>
            <span className="text-[14px] text-slate-400">MPa</span>
          </div>
        </Card>
      </div>

      {/* Subsystem status cards */}
      <div className="mb-8 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
        {data.subsystems.map((s) => (
          <SubsystemCard key={s.id} name={s.name} health={s.health} onOpen={() => nav(SUBSYS_ROUTE[s.id] ?? '/')} />
        ))}
      </div>

      {/* Process overview */}
      <section className="mb-8">
        <SectionTitle title="全场工艺总览" desc="定稿新流向；非等比例示意。与下方「快链」及侧栏子页一致。" />
        <ProcessChain nodes={data.processChain} />
        <div className="mt-2 text-[12px] text-slate-400">定稿源水新流向（示意，以签章工艺为准）</div>
      </section>

      {/* Lab / RAS7 / alarm / ops */}
      <section>
        <SectionTitle title="化验 · 7 号代表值 · 报警 · 经营" />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <Card className="p-5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[15px] font-semibold text-slate-800">化验与时效</span>
              {data.lab.staleOver24h ? <Badge className="bg-warn-bg text-warn-fg border-warn-border">&gt;24h 已标黄</Badge> : <Badge>正常区</Badge>}
              <span className="text-[12px] text-slate-400">来自 APP 化验</span>
            </div>
            <div className="mt-4 space-y-2 text-[15px]">
              <div className="flex justify-between"><span className="text-slate-500">氨氮</span><b className="tabular-nums">{fmt(data.lab.nh3n, 2)}</b></div>
              <div className="flex justify-between"><span className="text-slate-500">亚盐</span><b className="tabular-nums">{fmt(data.lab.no2, 2)}</b></div>
            </div>
            <div className="mt-4 text-[12px] text-slate-400">采样/入站 · {ts(data.lab.sampleAt)}</div>
          </Card>

          <Card className="p-5">
            <div className="text-[15px] font-semibold text-slate-800">7 号 RAS 摘要</div>
            <div className="mt-4 grid grid-cols-3 gap-4 text-center">
              <div><div className="text-[12px] text-slate-400">DO</div><div className="text-[22px] font-bold tabular-nums">{fmt(data.ras7.do, 1)}</div></div>
              <div><div className="text-[12px] text-slate-400">T</div><div className="text-[22px] font-bold tabular-nums">{fmt(data.ras7.t, 1)}</div></div>
              <div><div className="text-[12px] text-slate-400">pH</div><div className="text-[22px] font-bold tabular-nums">{fmt(data.ras7.ph, 2)}</div></div>
            </div>
            <div className="mt-4 flex justify-center gap-8 text-[14px] text-slate-600">
              <span>氨氮 <b className="tabular-nums">{fmt(data.ras7.nh3n, 2)}</b></span>
              <span>亚盐 <b className="tabular-nums">{fmt(data.ras7.no2, 2)}</b></span>
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 border-t border-hairline pt-3 text-[13px] text-slate-500">
              活动报警
              <span className="pill bg-alarm-bg text-alarm-fg border border-alarm-border">{data.alarmCounts.active}（一级 {data.alarmCounts.l1} · 二级 {data.alarmCounts.l2}）</span>
              <button onClick={() => nav('/alarms')} className="text-brand-600 hover:underline">进入报警中心 →</button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}

/** A2: live badge showing whether the newest collected data is fresh or stalled. */
function DataHealthChip() {
  const { data } = useDataHealth();
  if (!data) return <span className="text-slate-400">数据源：检测中…</span>;
  if (data.healthy) {
    return <Badge className="bg-ok-bg text-ok-fg border-ok-border">数据源正常 · {data.mode === 'sim' ? '仿真' : '采集'} · {data.lastDataAgeSec}s</Badge>;
  }
  return <Badge className="bg-warn-bg text-warn-fg border-warn-border">数据源陈旧/离线 · 已 {data.lastDataAgeSec < 0 ? '从未收到' : `${data.lastDataAgeSec}s`}</Badge>;
}
